import { useRef, useState } from "react";
import styled from "styled-components";

interface DragableDivProps {
  scrollVisible?: boolean;
  children: React.ReactNode;
}

const Container = styled.div`
  overflow-x: hidden;

  > * {
    user-select: none;
  }
`;

const ScrollContainer = styled.div`
  overflow-y: hidden;

  > * {
    user-select: none;
    -webkit-user-select: none; /* Safari */
    -ms-user-select: none; /* IE 10 and IE 11 */
  }
`;

function DragableDiv({ children, scrollVisible }: DragableDivProps) {
  const [dragging, setDragging] = useState(false);
  const [startX, setStartX] = useState<number | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);

  const divRef = useRef<HTMLDivElement>(null);

  function handleMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    setDragging(true);
    setStartX(e.pageX - (divRef.current?.offsetLeft ?? 0));
    setScrollLeft(divRef.current?.scrollLeft ?? 0);
  }

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!dragging || !divRef.current) return;
    const x = e.pageX - (divRef.current?.offsetLeft ?? 0);
    const walk = (x - (startX ?? 0)) * 1; // adjust scrolling speed here
    divRef.current.scrollLeft = scrollLeft - walk;
  }

  function handleMouseUp() {
    setDragging(false);
  }

  if (scrollVisible) {
    return (
      <ScrollContainer
        ref={divRef}
        style={{
          WebkitOverflowScrolling: "touch",
          cursor: dragging ? "grabbing" : "grab",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => setDragging(false)}
      >
        {children}
      </ScrollContainer>
    )
  }

  return (
    <Container
      ref={divRef}
      style={{
        WebkitOverflowScrolling: "touch",
        cursor: dragging ? "grabbing" : "grab",
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => setDragging(false)}
    >
      {children}
    </Container>
  );
}

export default DragableDiv;
